import { useRef, useCallback, useState } from 'react';

interface UseDragDropOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onDrop: (files: FileList) => void;
  enabled?: boolean;
}

function hasFileDragPayload(event: React.DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('Files');
}

export function useDragDrop({ containerRef, onDrop, enabled = true }: UseDragDropOptions) {
  const dragCounter = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!enabled || !hasFileDragPayload(e)) return;
      dragCounter.current++;
      if (dragCounter.current === 1) {
        containerRef.current?.classList.add('drag-active');
        setIsDraggingFiles(true);
      }
    },
    [containerRef, enabled]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!enabled || !hasFileDragPayload(e)) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) {
        containerRef.current?.classList.remove('drag-active');
        setIsDraggingFiles(false);
      }
    },
    [containerRef, enabled]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!enabled || !hasFileDragPayload(e)) return;
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    [enabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!enabled || !hasFileDragPayload(e)) return;
      dragCounter.current = 0;
      containerRef.current?.classList.remove('drag-active');
      setIsDraggingFiles(false);
      if (e.dataTransfer.files.length > 0) {
        onDrop(e.dataTransfer.files);
      }
    },
    [containerRef, onDrop, enabled]
  );

  return {
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    isDraggingFiles,
  };
}
